var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import classNames from 'classnames';
import React, { forwardRef, memo, useCallback, useContext, useMemo } from 'react';
import ConfigProvider from '@baifendian/adhere-ui-configprovider';
import Auto from './Auto';
import BackLayout from './BackLayout';
import { FlexContext } from './Context';
import Fixed from './Fixed';
import HorizontalFlexLayout from './HorizontalFlexLayout';
import ScrollLayout, { ScrollLayoutContext, useScrollLayout } from './ScrollLayout';
import * as TRBLC from './TRBLC';
import ToolBarLayout from './ToolBarLayout';
import { getValueWithUnit } from './Util';
import VerticalFlexLayout from './VerticalFlexLayout';
export var selectorPrefix = 'adhere-ui-flex-layout';
/**
 * InternalFlexLayout
 * @param {FlexLayoutProps} props
 * @param ref
 * @constructor
 */
var InternalFlexLayout = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
    style = props.style,
    direction = props.direction,
    gutter = props.gutter,
    children = props.children,
    attrs = __rest(props, ["className", "style", "direction", "gutter", "children"]);
  var media = useContext(ConfigProvider.Context).media;
  var targetDirection = useMemo(function () {
    return direction !== null && direction !== void 0 ? direction : 'vertical';
  }, [direction]);
  /**
   * getVerticalGridStyle
   */
  var getVerticalGridStyle = useCallback(function () {
    return {};
  }, []);
  /**
   * getHorizontalGridStyle
   */
  var getHorizontalGridStyle = useCallback(function () {
    var rowGapOrigin = 0;
    var columnGapOrigin = 0;
    if (Array.isArray(gutter)) {
      if (gutter.length === 1) {
        rowGapOrigin = gutter[0];
        columnGapOrigin = gutter[0];
      } else if (gutter.length === 2) {
        rowGapOrigin = gutter[0];
        columnGapOrigin = gutter[1];
      }
    } else {
      rowGapOrigin = gutter;
      columnGapOrigin = gutter;
    }
    var columnGapOriginValue = getValueWithUnit(columnGapOrigin / 2, media);
    return {
      rowGap: getValueWithUnit(rowGapOrigin, media),
      marginLeft: "-".concat(columnGapOriginValue),
      marginRight: "-".concat(columnGapOriginValue)
    };
  }, [gutter, media]);
  /**
   * getGridStyle
   */
  var getGridStyle = useCallback(function () {
    var _a;
    var map = new Map([['horizontal', getHorizontalGridStyle], ['vertical', getVerticalGridStyle]]);
    return (_a = map.get(targetDirection)) === null || _a === void 0 ? void 0 : _a();
  }, [targetDirection, getHorizontalGridStyle, getVerticalGridStyle]);
  // class
  var classList = useMemo(function () {
    return classNames(selectorPrefix, className, "".concat(selectorPrefix, "-").concat(targetDirection));
  }, [className, targetDirection]);
  // style
  var styleList = useMemo(function () {
    var defaultStyle = style !== null && style !== void 0 ? style : {};
    // 栅格的style
    var gridStyle = 'gutter' in props ? getGridStyle() : {};
    return __assign(__assign({}, defaultStyle), gridStyle);
  }, [style, gutter, getGridStyle]);
  return /*#__PURE__*/React.createElement(FlexContext.Provider, {
    value: {
      gutter: gutter,
      direction: targetDirection,
      children: children
    }
  }, /*#__PURE__*/React.createElement("div", __assign({
    ref: ref
  }, attrs, {
    className: classList,
    style: styleList
  }), children));
}));
var FlexLayout = InternalFlexLayout;
FlexLayout.displayName = 'FlexLayout';
FlexLayout.selectorPrefix = selectorPrefix;
FlexLayout.Context = FlexContext;
FlexLayout.Fixed = Fixed;
FlexLayout.Auto = Auto;
FlexLayout.HorizontalFlexLayout = HorizontalFlexLayout;
FlexLayout.VerticalFlexLayout = VerticalFlexLayout;
FlexLayout.ToolBarLayout = ToolBarLayout;
FlexLayout.BackLayout = BackLayout;
FlexLayout.ScrollLayout = ScrollLayout;
FlexLayout.useScrollLayout = useScrollLayout;
FlexLayout.ScrollLayoutContext = ScrollLayoutContext;
FlexLayout.TRBLC = TRBLC;
export default FlexLayout;